import { zodResolver } from '@hookform/resolvers/zod'
import { LogOut, UserRound } from 'lucide-react'
import { useState, type ReactElement } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { z } from 'zod'

import { parentAuthChangePasswordMutation } from '@/client/@tanstack/react-query.gen'
import { useMe } from '@/auth/queries'
import { useSignOut } from '@/auth/use-sign-out'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'

const passwordSchema = z
  .object({
    current: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(8, 'New password must be at least 8 characters'),
    confirm: z.string().min(1, 'Confirm the new password'),
  })
  .refine((values) => values.newPassword === values.confirm, {
    message: 'Passwords do not match',
    path: ['confirm'],
  })

type PasswordValues = z.infer<typeof passwordSchema>

type AccountSheetProps = {
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

function errorDetail(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) {
    return null
  }
  const detail = Reflect.get(error, 'detail')
  return typeof detail === 'string' ? detail : null
}

export function AccountSheet({ open, onOpenChange }: AccountSheetProps = {}): ReactElement {
  const me = useMe()
  const { signOut, isPending } = useSignOut()
  const [internalOpen, setInternalOpen] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [changed, setChanged] = useState(false)

  const form = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { current: '', newPassword: '', confirm: '' },
  })

  const changePassword = useMutation({
    ...parentAuthChangePasswordMutation(),
    onSuccess: () => {
      setServerError(null)
      setChanged(true)
      form.reset()
    },
    onError: (error) => {
      setChanged(false)
      setServerError(errorDetail(error))
    },
  })

  const onSubmit = (values: PasswordValues): void => {
    setServerError(null)
    setChanged(false)
    changePassword.mutate({ body: { current: values.current, new: values.newPassword } })
  }

  return (
    <Sheet
      open={open ?? internalOpen}
      onOpenChange={(next) => {
        if (open === undefined) {
          setInternalOpen(next)
        }
        if (next) {
          setServerError(null)
          setChanged(false)
          form.reset()
        }
        onOpenChange?.(next)
      }}
    >
      {open === undefined && (
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Account">
            <UserRound aria-hidden="true" className="size-5" />
          </Button>
        </SheetTrigger>
      )}
      <SheetContent side="bottom" className="mx-auto max-w-md">
        <SheetHeader>
          <SheetTitle>{me.data?.name ?? 'Account'}</SheetTitle>
          <SheetDescription>{me.data?.email}</SheetDescription>
        </SheetHeader>
        <form className="space-y-3 px-4 pb-2" onSubmit={form.handleSubmit(onSubmit)}>
          <Input
            type="password"
            aria-label="Current password"
            placeholder="Current password"
            autoComplete="current-password"
            {...form.register('current')}
          />
          <Input
            type="password"
            aria-label="New password"
            placeholder="New password"
            autoComplete="new-password"
            {...form.register('newPassword')}
          />
          <Input
            type="password"
            aria-label="Confirm new password"
            placeholder="Confirm new password"
            autoComplete="new-password"
            {...form.register('confirm')}
          />
          {(Object.values(form.formState.errors) as { message?: string }[])
            .filter((error) => error.message !== undefined)
            .map((error) => (
              <p key={error.message} role="alert" className="text-destructive text-sm">
                {error.message}
              </p>
            ))}
          {serverError !== null && (
            <p role="alert" className="text-destructive text-sm">
              {serverError}
            </p>
          )}
          {changed && <p className="text-sm">Password changed. Other devices are signed out.</p>}
          <Button type="submit" disabled={changePassword.isPending} className="w-full">
            Change password
          </Button>
        </form>
        <div className="px-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <Button
            variant="outline"
            className="w-full"
            onClick={signOut}
            disabled={isPending}
          >
            <LogOut aria-hidden="true" className="mr-2 size-4" />
            Sign out
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
